// T117 — One shared "AI is working" primitive: animated dot + verb + ETA + cost.
// The cost/ETA copy lives here as the single source of truth so the static
// "Cost: ~$X per generation" lines and the in-flight busy state can never drift.
// Costs are Sonnet display estimates (same basis as Settings' autoExplain
// $0.005–$0.02/explain formula) — not read from the server. Keep the `~` prefix.
//
// The pulse dot reuses the canonical idiom (SkillGapPanel/MorningBriefPanel) so
// it adopts every theme via the brand token.
export const AI_COST = {
  coverLetter: { eta: '~10s', cost: '~$0.015', verb: 'Writing cover letter' },
  tailoredCv: { eta: '~8s', cost: '~$0.005', verb: 'Tailoring summary' },
  cvVariants: { eta: '~12s', cost: '~$0.01', verb: 'Drafting two variants' },
  interviewPrep: { eta: '~15s', cost: '~$0.025', verb: 'Building prep pack' },
  companyResearch: { eta: '~12s', cost: '~$0.02', verb: 'Researching company' },
  cvParse: { eta: '~30s', cost: '~$0.02', verb: 'Parsing CV' },
  linkedinPdf: { eta: '~20s', cost: '~$0.015', verb: 'Parsing LinkedIn PDF' },
};

// Inline busy indicator. `feature` keys into AI_COST; `compact` drops the cost
// line (for tight button rows). Pass `label` to override the verb.
export default function AiBusy({ feature, label, compact = false, className = '' }) {
  const c = AI_COST[feature] ?? { eta: '', cost: '', verb: 'Asking AI' };
  return (
    <span
      className={'inline-flex items-center text-brand-300 ' + className}
      role="status"
      aria-live="polite"
    >
      <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-brand-500" />
      <span>{label ?? c.verb}…</span>
      {(c.eta || c.cost) && (
        <span className="ml-2 text-[11px] text-brand-400/70">
          {c.eta}
          {!compact && c.cost ? ` · ${c.cost}` : ''}
        </span>
      )}
    </span>
  );
}

// Tiny static cost tag for empty-state copy. Reads the same map.
export function AiCostTag({ feature }) {
  const c = AI_COST[feature];
  if (!c?.cost) return null;
  return (
    <span className="text-xs text-slate-500">
      {c.cost} per generation · {c.eta}
    </span>
  );
}
